import React, { useState } from 'react';
import { api } from '../lib/api';
import { fmtDate, money } from '../lib/format';
import { TEXT_LANGUAGES, type Tenant, type TextLanguage } from '../lib/types';
import { Confirm, Drawer, Failed, Loading, type Ctx } from '../main';
import { useResource } from '../lib/hooks';

export function Tenants({ ctx }: { ctx: Ctx }): React.JSX.Element {
  const { data, loading, error, reload } = useResource(() => api.tenants(), []);
  const [form, setForm] = useState<Tenant | 'new' | null>(null);
  const [open, setOpen] = useState<Tenant | null>(null);
  const [removing, setRemoving] = useState<Tenant | null>(null);
  const [query, setQuery] = useState('');
  if (loading) return <Loading />;
  if (error) return <Failed error={error} onRetry={reload} />;
  const all = data?.tenants ?? [];
  const q = query.trim().toLowerCase();
  const tenants = q ? all.filter((t) => `${t.name} ${t.phone} ${t.property_name ?? ''}`.toLowerCase().includes(q)) : all;

  return (
    <>
      <div className="bar">
        <input className="search" placeholder="Search name, phone or property" value={query}
          onChange={(e) => setQuery(e.target.value)} aria-label="Search tenants" />
        <span className="sub">{all.length} resident{all.length === 1 ? '' : 's'}</span>
        {ctx.canManage && (
          <button className="btn sm accent w" style={{ marginLeft: 'auto' }} onClick={() => setForm('new')}>
            Add a tenant
          </button>
        )}
      </div>

      <div className="card"><table className="responsive">
        <thead><tr><th>Name</th><th>Phone</th><th>Where</th><th>Rent</th><th>Lease ends</th></tr></thead>
        <tbody>{tenants.map((t) => (
          <tr key={t.id} className="clk" onClick={() => setOpen(t)}>
            <td data-label="Name"><b>{t.name}</b>{t.nationality && <div className="sub">{t.nationality}</div>}</td>
            <td data-label="Phone" className="mono">{t.phone}</td>
            <td data-label="Where">
              {t.property_name ? `${t.property_name} · Room ${t.room_number ?? ''}` : <span className="sub">no lease</span>}
            </td>
            <td data-label="Rent" className="mono">{t.monthly_rent ? money(Number(t.monthly_rent)) : '—'}</td>
            <td data-label="Lease ends" className="mono">{fmtDate(t.end_date)}</td>
          </tr>
        ))}</tbody>
      </table>
      {tenants.length === 0 && (
        <div className="empty"><b>{q ? 'No match' : 'No tenants'}</b>
          {q ? 'Nobody matches that search.' : 'A tenant is usually added when a viewing turns into a deal.'}</div>
      )}
      </div>

      <div className="note" style={{ marginTop: 16 }}>
        A tenant sees only their own lease and payments. Nothing on this screen is visible to them —
        the API refuses a tenant’s token on every staff route.
      </div>

      {open && !form && !removing && (
        <Drawer title={open.name} subtitle={open.phone} onClose={() => setOpen(null)}
          footer={<>
            {ctx.canManage && (
              <button className="btn quiet w" style={{ marginRight: 'auto', color: 'var(--coral)' }}
                disabled={Boolean(open.lease_id)} title={open.lease_id ? 'On an active lease' : undefined}
                onClick={() => setRemoving(open)}>Delete</button>
            )}
            <button className="btn" onClick={() => setOpen(null)}>Close</button>
            {ctx.canManage && <button className="btn accent w" onClick={() => setForm(open)}>Edit</button>}
          </>}>
          <dl className="kv">
            <dt>Phone</dt><dd className="mono">{open.phone}</dd>
            <dt>Email</dt><dd>{open.email ?? '—'}</dd>
            <dt>Account</dt><dd>{open.account === 'active' ? 'Signed up'
              : open.email ? 'Not signed up yet: they create it with this email' : 'No email: they cannot sign in until one is added'}</dd>
            <dt>Nationality</dt><dd>{open.nationality ?? '—'}</dd>
            <dt>Messages in</dt><dd>{TEXT_LANGUAGES[open.language ?? 'en']}</dd>
            <dt>Lives at</dt><dd>{open.property_name ? `${open.property_name} · Room ${open.room_number ?? ''}` : 'No current lease'}</dd>
            {open.monthly_rent && <><dt>Rent</dt><dd className="mono">{money(Number(open.monthly_rent))}/mo</dd></>}
            {open.end_date && <><dt>Lease ends</dt><dd className="mono">{fmtDate(open.end_date)}</dd></>}
          </dl>
          {open.lease_id && (
            <div className="note" style={{ marginTop: 14 }}>
              On an active lease, so this record cannot be deleted. Start the move-out from Leases.
            </div>
          )}
        </Drawer>
      )}

      {form && (
        <TenantForm ctx={ctx} tenant={form === 'new' ? null : form} onClose={() => setForm(null)}
          onSaved={async () => { setForm(null); setOpen(null); await reload(); }} />
      )}
      {removing && (
        <Confirm title={`Delete ${removing.name}?`} danger action="Delete tenant"
          detail="Only for someone who never became a resident. Anyone with lease history is kept — that history is the record — and the API will refuse."
          onClose={() => setRemoving(null)}
          onConfirm={async () => {
            await api.deleteTenant(removing.id);
            setRemoving(null); setOpen(null); await reload();
            ctx.toast('Tenant deleted', removing.name);
          }} />
      )}
    </>
  );
}

function TenantForm({ ctx, tenant, onClose, onSaved }: {
  ctx: Ctx; tenant: Tenant | null; onClose: () => void; onSaved: () => Promise<void>;
}): React.JSX.Element {
  const [form, setForm] = useState({
    name: tenant?.name ?? '', phone: tenant?.phone ?? '',
    email: tenant?.email ?? '', nationality: tenant?.nationality ?? '',
  });
  const [language, setLanguage] = useState<TextLanguage>(tenant?.language ?? 'en');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async (): Promise<void> => {
    setBusy(true); setError(null);
    try {
      // on an edit, an emptied field is sent as null, which clears it; leaving it
      // out would keep the old value while this form reported it saved. The email
      // is never cleared: it is how they sign in.
      const cleared = tenant ? null : undefined;
      const body = {
        name: form.name, phone: form.phone,
        email: form.email.trim(), nationality: form.nationality || cleared, language,
      };
      if (tenant) await api.updateTenant(tenant.id, body); else await api.createTenant(body);
      ctx.toast(tenant ? 'Tenant saved' : 'Tenant added',
        tenant ? form.name : 'They create their account at the tenant site or app with that email.');
      await onSaved();
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
    }
  };

  return (
    <Drawer title={tenant ? `Edit ${tenant.name}` : 'Add a tenant'} onClose={onClose}
      footer={<>
        <button className="btn" onClick={onClose}>Cancel</button>
        <button className="btn accent" disabled={!form.name || !form.phone || !form.email.includes('@') || busy} onClick={save}>
          {busy ? 'Saving…' : tenant ? 'Save changes' : 'Add'}
        </button>
      </>}>
      {error && <div className="note bad" style={{ marginBottom: 14 }}>{error}</div>}
      <div className="note">
        They sign in with their email. The first time, they confirm the email with a code, the phone
        with a text message, and choose a password — so get both right, the phone in full international
        format.{tenant ? ' A changed email or phone is confirmed again at their next sign-in.' : ''}
      </div>
      {([['name', 'Full name', 'text'], ['phone', 'Phone', 'tel'],
         ['email', 'Email', 'email'], ['nationality', 'Nationality (optional)', 'text']] as const)
        .map(([field, label, type]) => (
          <div className="field" key={field} style={{ marginTop: 12 }}>
            <label htmlFor={`t-${field}`}>{label}</label>
            <input id={`t-${field}`} type={type} value={form[field]}
              placeholder={field === 'phone' ? '+971 50 123 4567' : undefined}
              onChange={(e) => setForm({ ...form, [field]: e.target.value })} />
          </div>
        ))}
      <div className="field" style={{ marginTop: 12 }}>
        <label htmlFor="t-language">Messages in</label>
        <select id="t-language" value={language} onChange={(e) => setLanguage(e.target.value as TextLanguage)}>
          {(Object.keys(TEXT_LANGUAGES) as TextLanguage[]).map((l) => (
            <option key={l} value={l}>{TEXT_LANGUAGES[l]}</option>
          ))}
        </select>
        <div className="hint">Texts and emails go out in this language. It follows whatever they pick when they sign in.</div>
      </div>
    </Drawer>
  );
}
